import React from 'react'
import {useNavigate} from 'react-router-dom'
import type {AgentStep} from './hooks'
import {tryParseCloudPath} from '@/shared/utils/cloudPath'
import {S3Image} from './components/S3Image'
import {cloudPathFromAppHref} from './cloudLinkKind'
import {CloudPathLink} from './cloudPathLinks'
import {formatAgentXmlToMarkdown} from './formatAgentXmlToMarkdown'
import {replaceOutsideCode} from './markdownCodeRanges'

export {formatAgentXmlToMarkdown, formatAssistantDisplayMarkdown, type AgentAnswerFormat} from './formatAgentXmlToMarkdown'

/** Strip XML step tags and convert to markdown (fenced code for execute / observation). Used by tool steps and timeline text. */
export function formatStepContentForMarkdown(step: AgentStep): string {
  const base = step.content?.trim() || step.assistantContent || step.observation || ''
  return formatAgentXmlToMarkdown(base)
}

const CLOUD_PREFIX = 'cloud:'
/**
 * `](cloud:…)` link/image destination. One with a space is only valid CommonMark inside `<…>`.
 * Lengths are bounded so a long line without a closing `)` cannot make the scan quadratic.
 */
const CLOUD_DEST_RE = /\]\((cloud:[^)<>"\n]{1,2048})\)/g
const CLOUD_IMAGE_MD_RE = /!\[[^\]\n]{0,1024}\]\((?:<cloud:[^>\n]{0,2048}>|cloud:[^)\n]{1,2048})\)/g

function isBlank(ch: string): boolean {
  return ch === ' ' || ch === '\t'
}

/** Line breaks to put before an image at `offset` so it starts its own paragraph. */
function breakBefore(markdown: string, offset: number): string {
  let i = offset
  while (i > 0 && isBlank(markdown[i - 1])) i--
  if (i === 0) return ''
  if (markdown[i - 1] !== '\n') return '\n\n'
  return i > 1 && markdown[i - 2] === '\n' ? '' : '\n'
}

/**
 * Prepares assistant markdown for `cloud:` links and images: destinations with spaces (valid in
 * cloud paths, which are never URL-encoded) are wrapped in `<…>` so they still parse, and inline
 * images get their own paragraph. Fenced code blocks and inline code are left untouched.
 */
export function prepareCloudMarkdown(markdown: string): string {
  if (!markdown.includes(CLOUD_PREFIX)) return markdown
  const wrapped = replaceOutsideCode(markdown, CLOUD_DEST_RE, (match, _offset, _whole, [dest]) =>
    dest.includes(' ') ? `](<${dest}>)` : match
  )
  return replaceOutsideCode(
    wrapped,
    CLOUD_IMAGE_MD_RE,
    (image, offset, whole) => `${breakBefore(whole, offset)}${image}`
  )
}

/** Schemes a markdown link may use; anything else (javascript:, data:, vbscript:, …) is dropped. */
const LINK_SCHEMES: ReadonlySet<string> = new Set(['http', 'https', 'mailto', 'cloud'])
/** Schemes a markdown image (any `src`) may load from. Relative URLs are not allowed either. */
const IMAGE_SCHEMES: ReadonlySet<string> = new Set(['https', 'cloud'])
/** ASCII control characters, space and DEL: browsers ignore some of them inside a scheme. */
// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE_RE = /[\u0000-\u0020\u007f]/g
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i

/** Lower-case scheme of a URL, or `null` for a relative one (`/…`, `#…`, `?…`, `a/b.txt`). */
function urlScheme(url: string): string | null {
  const match = SCHEME_RE.exec(url.replace(CONTROL_OR_SPACE_RE, ''))
  return match ? match[1].toLowerCase() : null
}

/**
 * ReactMarkdown URL sanitizer (an allowlist). Links may be http:, https:, mailto:, cloud: or
 * relative; images (`src`) only cloud: or https:. Anything else becomes `''`.
 */
export function markdownUrlTransform(url: string, key?: string): string {
  const scheme = urlScheme(url)
  if (key === 'src') return scheme !== null && IMAGE_SCHEMES.has(scheme) ? url : ''
  return scheme === null || LINK_SCHEMES.has(scheme) ? url : ''
}

const headingAsParagraph = {
  h1: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
  h2: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
  h3: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
  h4: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
  h5: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
  h6: ({children}: {children?: React.ReactNode}) => (
    <p>
      <strong>{children}</strong>
    </p>
  ),
}

const s3ImageComponent = {
  img: ({src, alt}: {src?: string; alt?: string}) => {
    if (src?.startsWith(CLOUD_PREFIX)) {
      // Amplify presigns `<identityId>/<key>`, so the root-relative path is the key.
      const parsed = tryParseCloudPath(src)
      if (!parsed || parsed.isFolder) {
        return <span className='omics-cloud-image--invalid'>{alt || src}</span>
      }
      return <S3Image s3Key={parsed.rel} alt={alt} />
    }
    return <img src={src} alt={alt} />
  },
}

/**
 * Same-origin OmicsBox app deep links from the MCP resolve here for in-tab SPA navigation:
 * `/results?file=` (the `open_url` viewer) and `/files?path=` (the `open_dir_url` Files browser).
 */
function resolveInternalResultsPath(href?: string): string | null {
  if (!href) return null
  try {
    const url = new URL(href, window.location.origin)
    const isAppRoute = url.pathname.startsWith('/results') || url.pathname.startsWith('/files')
    if (url.origin === window.location.origin && isAppRoute) {
      return url.pathname + url.search
    }
  } catch {
    return null
  }
  return null
}

/**
 * Renders assistant-message links. Links into the user's storage — `cloud:` paths and the
 * same-origin `/results?file=` / `/files?path=` deep links the MCP mints — all render as one
 * decorated CloudPathLink (icon by kind, chip style) and open the file in the viewer or the folder
 * in the Files panel, wherever they sit (paragraph, list, table). Other same-origin app routes
 * navigate in the same tab; external links open in a new tab.
 */
function AssistantLink({href, children, ...props}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const cloudPath = href?.startsWith(CLOUD_PREFIX)
    ? href
    : cloudPathFromAppHref(href, window.location.origin)
  if (cloudPath) {
    return (
      <CloudPathLink path={cloudPath} decorated {...props}>
        {children}
      </CloudPathLink>
    )
  }
  return (
    <WebLink href={href} {...props}>
      {children}
    </WebLink>
  )
}

function WebLink({href, children, ...props}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const navigate = useNavigate()
  const internalPath = resolveInternalResultsPath(href)
  if (internalPath) {
    return (
      <a
        href={href}
        {...props}
        onClick={(e) => {
          // Honor modifier / non-left clicks so users can still open a new tab intentionally.
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
          e.preventDefault()
          navigate(internalPath)
        }}
      >
        {children}
      </a>
    )
  }
  return (
    <a href={href} target='_blank' rel='noopener noreferrer' {...props}>
      {children}
    </a>
  )
}

const linkComponent = {
  a: AssistantLink,
}

/** Step / reasoning markdown: flat headings, S3 thumbnails, safe links. */
export const agentStepMarkdownComponents = {...headingAsParagraph, ...s3ImageComponent, ...linkComponent}

/** Final answer bubble: images + links only (no heading flattening). */
export const chatInlineMarkdownComponents = {...s3ImageComponent, ...linkComponent}
